/*eslint react-hooks/exhaustive-deps: "off"*/
import React, { useEffect, useState } from "react";
import { Col, Modal, Row, Select, Table, Typography } from "antd";
import useMapProps from "../useMapProps";
import { MapPropsProvider } from "../MapPropsContext";
import MapBase from "../MapBase";
import WidgetDistrictSelect from "./WidgetDistrictSelect";
import WidgetLegend from "./WidgetLegend";
import { OPTIONS as ORIGIN_CLS_CODE_OPTIONS } from "./WidgetClsCodeForStats";
import { DISTRICT_LV1_OPTIONS } from "./WidgetDistrictSelect";
import {
  numberWithCommas,
  rgbToArray,
  rgbToCssRgbFunction,
} from "../../../utils";
import "./WidgetStatsAnalysisModal.scss";
import { JSON_SIGUNGU, TI_SELECT_KUK_JONG } from "../../../Constants";

const TOTAL_CLS_CODE = "0";
const CLS_CODE_OPTIONS = [
  { cls_code: TOTAL_CLS_CODE, label: "전체" },
  ...ORIGIN_CLS_CODE_OPTIONS,
];
const { Text } = Typography;

const ZERO_COLOR = "#FFFFFFaa";
const MIN_COLOR = "#FF0000aa";
const FILL_COLOR_POLYGON = [
  [rgbToArray("#111c54aa"), "20% 이상", 20],
  [rgbToArray("#2a387caa"), "15% ~ 20%", 15],
  [rgbToArray("#4757a8aa"), "10% ~ 15%", 10],
  [rgbToArray("#6678cdaa"), "5% ~ 10%", 5],
  [rgbToArray("#8494e3aa"), "0% ~ 5%", 0],
  [rgbToArray(ZERO_COLOR), "0%", null],
  [rgbToArray("#FFCCCCaa"), "0% ~ -5%", -5],
  [rgbToArray("#FF9999aa"), "-5% ~ -10%", -10],
  [rgbToArray("#FF6666aa"), "-10% ~ -15%", -15],
  [rgbToArray("#FF3333aa"), "-15% ~ -20%", -20],
  [rgbToArray(MIN_COLOR), "-20% 이상", null],
];

const FILL_COLOR_LEGEND = [
  [rgbToCssRgbFunction("#111c54aa"), "20% 이상", 20],
  [rgbToCssRgbFunction("#2a387caa"), "15% ~ 20%", 15],
  [rgbToCssRgbFunction("#4757a8aa"), "10% ~ 15%", 10],
  [rgbToCssRgbFunction("#6678cdaa"), "5% ~ 10%", 5],
  [rgbToCssRgbFunction("#8494e3aa"), "0% ~ 5%", 0],
  [rgbToCssRgbFunction(ZERO_COLOR), "0%", null],
  [rgbToCssRgbFunction("#FFCCCCaa"), "0% ~ -5%", -5],
  [rgbToCssRgbFunction("#FF9999aa"), "-5% ~ -10%", -10],
  [rgbToCssRgbFunction("#FF6666aa"), "-10% ~ -15%", -15],
  [rgbToCssRgbFunction("#FF3333aa"), "-15% ~ -20%", -20],
  [rgbToCssRgbFunction(MIN_COLOR), "-20% 이상", null],
];

export default function WidgetStatsAnalysisModal({
  visible,
  setVisible,
  currentDistrictCode,
  usingClsCodeList,
  firstData,
  secondData,
}) {
  const mapProps = useMapProps({
    INITIAL_GEOJSON_POLYGON_LAYER_PROPS: {
      getHoverHtml: (feature, mapDistrictStats) => {
        const [metricObject] = mapDistrictStats;
        const {
          properties: { pk, name },
        } = feature;
        const code = pk;
        const { value1, value2, diffPercent } = metricObject[code] || {};

        if (!diffPercent) return name;

        return `<strong>${name}</strong><br>
기준년월 무선국수: ${numberWithCommas(value1)}<br/>
비교년월 무선국수: ${numberWithCommas(value2)}<br/>
증감률: ${diffPercent}%
`;
      },
      getLevelFillColor: ({ properties: { pk } }, mapDistrictStats) => {
        const [metricObject] = mapDistrictStats;
        const code = pk;
        const { diffPercent } = metricObject[code] || {};
        if (!diffPercent) {
          return rgbToArray(ZERO_COLOR);
        } else {
          const row = FILL_COLOR_POLYGON.find(([color, label, boundary]) => {
            return boundary !== null && diffPercent > boundary;
          });
          const color = row ? row[0] : rgbToArray(MIN_COLOR);
          return color;
        }
      },
    },
  });

  const {
    setMapDistrictStats,
    isChangedMapDistrictStats,
    setMapCenter,
    viewState,
  } = mapProps;

  const [currentClsCode, setCurrentClsCode] = useState(
    CLS_CODE_OPTIONS[0].cls_code
  );
  const [clsCodeOptionsForSelect, setClsCodeOptionsForSelect] =
    useState(CLS_CODE_OPTIONS);
  const [tableData, setTableData] = useState();
  const [districtLv2Options, setDistrictLv2Options] = useState();

  useEffect(() => {
    if (currentDistrictCode && currentDistrictCode.length === 2) {
      const districtLv2List = JSON_SIGUNGU.filter(
        ({ SID_CD }) => SID_CD === currentDistrictCode
      );
      setDistrictLv2Options(
        districtLv2List.map(({ SIG_CD, SIG_KOR_NM }) => ({
          value: SIG_CD,
          label: SIG_KOR_NM,
        }))
      );
      /*
      const url = getApiHost() + '/address/getSigugun.json';
      const params = {ctprvn_cd: currentDistrictCode};
      Axios.get(url, {params})
        .then(({data}) => {
          const districtLv2List = data;
          setDistrictLv2Options(
            districtLv2List.map(({SIG_CD, SIG_KOR_NM}) => ({value: SIG_CD, label: SIG_KOR_NM}))
          );
        })
        .catch(e => console.error(e));
       */
    } else {
      setDistrictLv2Options(null);
    }
  }, [currentDistrictCode]);

  useEffect(() => {
    setClsCodeOptionsForSelect(
      CLS_CODE_OPTIONS.filter(
        ({ cls_code }) =>
          cls_code === "0" || usingClsCodeList.includes(cls_code)
      )
    );
  }, [usingClsCodeList]);

  useEffect(() => {
    const dataList1 = firstData.dataList;
    const dataList2 = secondData.dataList;

    if (visible && dataList1 && dataList2) {
      const newDataList1 = Object.entries(dataList1).reduce(
        (acc, [district_lv1_code, rows]) => {
          const total = Object.entries(rows)
            .filter(([cls_code]) => usingClsCodeList.includes(cls_code))
            .reduce((acc, [cls_code, count]) => acc + count, 0);
          acc[district_lv1_code] = { ...rows, [TOTAL_CLS_CODE]: total };
          return acc;
        },
        {}
      );

      const newDataList2 = Object.entries(dataList2).reduce(
        (acc, [district_lv1_code, rows]) => {
          const total = Object.entries(rows)
            .filter(([cls_code]) => usingClsCodeList.includes(cls_code))
            .reduce((acc, [cls_code, count]) => acc + count, 0);
          acc[district_lv1_code] = { ...rows, [TOTAL_CLS_CODE]: total };
          return acc;
        },
        {}
      );

      const tableData = (districtLv2Options || DISTRICT_LV1_OPTIONS)
        .filter(({ value }) => value.length > 0)
        .map(({ value, label }) => {
          const district_lv1_code = Math.floor(value);
          const value1 =
            (newDataList1[district_lv1_code] || {})[currentClsCode] || 0;
          const value2 =
            (newDataList2[district_lv1_code] || {})[currentClsCode] || 0;
          const diffPercent =
            value1 === 0 ? null : ((value2 / value1 - 1) * 100).toFixed(2);
          return {
            district_lv1_code,
            district_lv1_label: label,
            value1,
            value2,
            diffPercent,
          };
        })
        .filter(({ value1, value2 }) => value1 !== 0 || value2 !== 0);
      setTableData(tableData);

      const metricObject = tableData.reduce(
        (acc, { district_lv1_code, value1, value2, diffPercent }) => {
          acc[district_lv1_code] = { value1, value2, diffPercent };
          return acc;
        },
        {}
      );

      const newMapDistrictStats = [metricObject];
      if (isChangedMapDistrictStats(newMapDistrictStats)) {
        setMapDistrictStats(newMapDistrictStats);
      }
    }
  }, [visible, currentClsCode, firstData, secondData, setMapDistrictStats]);

  // 융합보기결과에서 국종선택은 검색조건에서 시도를 변경하면 디폴트값으로 전체가 나오게 설정하기
  useEffect(() => {
    if (visible) setCurrentClsCode(TOTAL_CLS_CODE);
  }, [visible]);

  const { longitude, latitude, zoom } = viewState;

  useEffect(() => {
    if (visible) {
      // 모달창 zoom 셋팅
      if (currentDistrictCode && currentDistrictCode.length === 2) {
        // 광역 단위
        setMapCenter({ latitude, longitude, zoom: 9 });
      } else {
        // 전국 단위
        setMapCenter({ latitude: 36.4806503, longitude: 127.2855187, zoom: 7 });
      }
    } else {
      // 모달창이 닫혀 있을 땐 viewState에 담겨 있는 zoom으로 셋팅합니다.
      setMapCenter({ latitude, longitude, zoom });
    }
  }, [
    visible,
    currentDistrictCode,
    viewState.longitude,
    viewState.latitude,
    viewState.zoom,
  ]);

  return (
    <Modal
      centered
      title={"지역별 무선국 증감 현황"}
      footer={null}
      width={1200}
      visible={visible}
      onOk={() => setVisible(false)}
      onCancel={() => setVisible(false)}
    >
      <MapPropsProvider value={mapProps}>
        <Row>
          <Col span={14}>
            <div tabIndex={-1}>
              <MapBase
                isHideControls={true}
                isIgnoreCenterChangeEvent={true}
                mapContainerStyle={{ width: "100%", height: 570 }}
              >
                <WidgetDistrictSelect
                  isShowDistrictLayerFromGeoserver={true}
                  hidden={true}
                  injectDistrictLv1Value={currentDistrictCode}
                />
              </MapBase>
              <WidgetLegend
                fillColorLegend={FILL_COLOR_LEGEND}
                style={{ right: 5, bottom: 5 }}
              />
            </div>
          </Col>
          <Col
            span={10}
            style={{ paddingLeft: 10 }}
            className={"select-modal-stats-analysis"}
          >
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              <Text strong style={{ width: "15%" }}>
                국종선택 :{" "}
              </Text>
              <Select
                value={`${currentClsCode}`}
                onChange={(value) => setCurrentClsCode(value)}
                style={{ width: "85%" }}
                // tabIndex={TI_SELECT_KUK_JONG}
              >
                {clsCodeOptionsForSelect.map(({ cls_code, label }) => (
                  <Select.Option value={cls_code} key={cls_code}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <Table
              dataSource={tableData}
              columns={[
                {
                  title: "지역",
                  dataIndex: "district_lv1_label",
                  key: "district_lv1_label",
                },
                {
                  title: (
                    <div>
                      기준년월
                      <br />
                      <small>
                        ({firstData.year}년 {firstData.month}월)
                      </small>
                    </div>
                  ),
                  dataIndex: "value1",
                  key: "value1",
                  render: (value1) => (
                    <div style={{ textAlign: "right" }}>
                      {numberWithCommas(value1)}
                    </div>
                  ),
                },
                {
                  title: (
                    <div>
                      비교년월
                      <br />
                      <small>
                        ({secondData.year}년 {secondData.month}월)
                      </small>
                    </div>
                  ),
                  dataIndex: "value2",
                  key: "value2",
                  render: (value2) => (
                    <div style={{ textAlign: "right" }}>
                      {numberWithCommas(value2)}
                    </div>
                  ),
                },
                {
                  title: "증감(%)",
                  dataIndex: "diffPercent",
                  key: "diffPercent",
                  width: 80,
                  render: (diffPercent) => (
                    <div style={{ textAlign: "right" }}>
                      {diffPercent || "-"}
                    </div>
                  ),
                },
              ]}
              rowKey={"district_lv1_label"}
              pagination={false}
              size={"small"}
              // scroll={{ y: 480 }}
              tableLayout={"fixed"}
            />
          </Col>
        </Row>
      </MapPropsProvider>
    </Modal>
  );
}
